/* eslint-disable react/jsx-curly-newline */
/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable indent */
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DynamicLoader from '@/atoms/dynamic-loader';
import useGetUserLocation from '@/common/hooks/useGetUserLocation';
import PropertyCard from '@/molecules/property-card';
import { DEFAULT_MAP_CENTER, fromGeoJson, toLatLng } from '@/utils/coordinates';
import { useGetUserLocationFromIP } from '@/utils/useGetUserLocationFromIP';
import { useGetAddressCoordinates } from '@core/offices/actions/properties/filterActions';
import { useGetProperties, useGetPropertyPaginator } from '@core/offices/actions/properties/propertiesActions';
import { useProperties } from '@core/offices/store/office';
import { OfficeDto, OfficePaginator } from '@graphql/hooks-and-types';

import { handleScrollToBottom } from './handleScroll';
import RentalMap from './Map';

const RentalResult: React.FC<{
  filteredPropertiesPaginator: OfficePaginator;
  isMapOpened: boolean;
  fetchMore: () => void;
  loading: boolean;
}> = ({ filteredPropertiesPaginator, isMapOpened, fetchMore, loading }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { location } = useGetUserLocation();
  const properties = useGetProperties();
  const propertyPaginatorInfo = useGetPropertyPaginator();
  const { setProperties } = useProperties();
  const addressCoordinates = useGetAddressCoordinates();
  const ipData = useGetUserLocationFromIP();

  const [focusedPropertyId, setFocusedPropertyId] = useState<string | null>(null);
  const [defaultCoordinates, setDefaultCoordinates] = useState(DEFAULT_MAP_CENTER);

  const focusOnMap = useCallback(
    (property: OfficeDto) => {
      if (focusedPropertyId === property.id) {
        window.open(`/rentals/${property.id}?clientName=${router.query.clientName}`, '_blank');
        return;
      }

      setFocusedPropertyId(property.id);
      const point = fromGeoJson(property.coordinates?.coordinates);
      if (point) setDefaultCoordinates(point);
    },
    [focusedPropertyId],
  );

  const handleScroll = useCallback((e) => handleScrollToBottom(e, fetchMore), [fetchMore]);
  useEffect(() => {
    // Pick the first source that yields a *valid* center; never store NaN/undefined,
    // which would crash the Google Maps projection.
    const firstPropertyPoint = properties.length > 0 ? fromGeoJson(properties[0].coordinates?.coordinates) : null;
    const addressPoint =
      properties.length === 0 && addressCoordinates ? toLatLng(addressCoordinates.lat, addressCoordinates.lng) : null;
    const userPoint = location ? toLatLng(location.latitude, location.longitude) : null;
    const point = firstPropertyPoint || addressPoint || userPoint;
    if (point) setDefaultCoordinates(point);
  }, [properties, location, addressCoordinates]);

  useEffect(() => {
    ipData.then((ipLocation) => {
      const ipPoint = toLatLng(ipLocation.latitude, ipLocation.longitude);
      if (ipPoint) {
        setDefaultCoordinates((current) => (current === DEFAULT_MAP_CENTER ? ipPoint : current));
      }
    });
    setProperties(filteredPropertiesPaginator.data, filteredPropertiesPaginator.paginatorInfo);
  }, []);

  return (
    <>
      {properties.length && !loading ? (
        <div
          onScroll={handleScroll}
          id="properties-list"
          className={`md:grid md:grid-cols-2 xl:grid-cols-4 md:gap-7 w-full p-2 lg:mx-9 mx-0 md:w-auto z-[39] bg-slate-50 md:bg-transparent mt-3 md:mt-5 rounded-md ${
            isMapOpened
              ? `no-scrollbar xl:!grid-cols-2 md:!grid-cols-1 md:!w-1/2 md:h-[${
                  window.screen.height * 0.7
                }px] md:overflow-y-auto absolute md:relative mt-0 md:mt-5 pt-0 md:!pt-2 lg:pe-[70px] md:pe-[15px] bg-transparent start-0 -bottom-5 md:bottom-0 overflow-auto flex flex-row`
              : ''
          }`}
          style={{
            maxHeight: window.screen.width >= 768 && isMapOpened ? window.screen.height * 0.7 : 'auto',
          }}
        >
          {properties.map((property) => (
            <PropertyCard
              isFocused={focusedPropertyId === property.id}
              key={property.id}
              onClick={() => focusOnMap(property as OfficeDto)}
              isRowView={isMapOpened && window.screen.width < 768}
              property={property as OfficeDto}
            />
          ))}
          {propertyPaginatorInfo?.hasMorePages && (
            <div className="w-full flex items-center justify-center">
              <DynamicLoader size="medium" />
            </div>
          )}
        </div>
      ) : (
        <div className="w-full md:w-1/2 flex justify-center items-center h-[80%] text-slate-500">
          {loading ? <DynamicLoader size="large" /> : t('rentals.noPropertiesFound')}
        </div>
      )}
      <RentalMap
        setFocusedPropertyId={setFocusedPropertyId}
        focusedPropertyId={focusedPropertyId}
        isMapOpened={isMapOpened}
        properties={properties}
        defaultCoordinates={defaultCoordinates}
      />
    </>
  );
};

export default RentalResult;
